import React, { useState, useRef } from 'react';
import { bulkCreateQuotations, validateQuotationNumberExists } from '../../firebase/quotationService';
import { auth } from '../../firebase/firebaseConfig';
import './CSVQuotationImport.css';

/**
 * CSVQuotationImport Component
 * Allows bulk import of historical quotations via CSV file upload
 */
const CSVQuotationImport = ({ onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [validationResults, setValidationResults] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  const [step, setStep] = useState('upload'); // upload, validate, import, complete
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef(null);

  // Handle file selection
  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
      parseCSV(selectedFile);
    } else {
      alert('Please select a valid CSV file');
    }
  };

  // Parse CSV file
  const parseCSV = (file) => {
    setProcessing(true);
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        
        if (lines.length < 2) {
          throw new Error('CSV file must contain at least a header row and one data row');
        }

        // Parse header
        const headers = lines[0].split(',').map(h => h.trim());
        const requiredHeaders = [
          'quotationNumber', 'quotationDate', 'companyName', 'attn', 'tel',
          'quotationType', 'paymentTerms', 'lineItemsJSON', 'subtotal', 'grandTotal'
        ];
        const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
        
        if (missingHeaders.length > 0) {
          throw new Error(`Missing required columns: ${missingHeaders.join(', ')}`);
        }

        // Parse data rows
        const data = [];
        for (let i = 1; i < lines.length; i++) {
          // Handle CSV with potential commas in JSON fields
          const line = lines[i];
          if (!line.trim()) continue;
          
          const row = parseCSVLine(line, headers);
          row.lineNumber = i + 1;
          data.push(row);
        }

        setCsvData(data);
        setStep('validate');
        validateData(data);
      } catch (error) {
        alert(`Error parsing CSV: ${error.message}`);
        setFile(null);
      } finally {
        setProcessing(false);
      }
    };

    reader.readAsText(file);
  };

  // Parse CSV line handling quoted fields with commas
  const parseCSVLine = (line, headers) => {
    const values = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    values.push(current.trim());
    
    const row = {};
    headers.forEach((header, index) => {
      row[header] = values[index] || '';
    });
    
    return row;
  };

  // Validate CSV data
  const validateData = async (data) => {
    setProcessing(true);
    
    try {
      const validationResults = [];
      
      for (const row of data) {
        const result = {
          lineNumber: row.lineNumber,
          data: row,
          errors: [],
          warnings: [],
          valid: true
        };

        // Validate quotation number format (BS-MMYYYY-XXX)
        const numberPattern = /^BS-\d{6}-\d{3}$/;
        if (!numberPattern.test(row.quotationNumber)) {
          result.errors.push('Quotation number must be in format BS-MMYYYY-XXX');
          result.valid = false;
        } else {
          // Check for duplicates
          try {
            const exists = await validateQuotationNumberExists(row.quotationNumber);
            if (exists) {
              result.errors.push(`Quotation number ${row.quotationNumber} already exists`);
              result.valid = false;
            }
          } catch (error) {
            result.warnings.push('Could not verify duplicate number');
          }
        }

        // Validate date format
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (!dateRegex.test(row.quotationDate)) {
          result.errors.push('Date must be in YYYY-MM-DD format');
          result.valid = false;
        } else {
          const date = new Date(row.quotationDate);
          if (isNaN(date.getTime())) {
            result.errors.push('Invalid quotation date');
            result.valid = false;
          }
        }

        // Validate required fields
        if (!row.companyName) {
          result.errors.push('Company name is required');
          result.valid = false;
        }
        if (!row.attn) {
          result.errors.push('Attention (attn) is required');
          result.valid = false;
        }
        if (!row.tel) {
          result.errors.push('Telephone is required');
          result.valid = false;
        }

        // Validate quotation type
        const validTypes = ['purchase', 'rental', 'rental_extension', 'scaffolding_service'];
        if (!validTypes.includes(row.quotationType)) {
          result.errors.push(`Invalid quotation type. Must be one of: ${validTypes.join(', ')}`);
          result.valid = false;
        }

        // Validate line items JSON
        try {
          // Escape actual newlines/tabs to JSON-compatible format
          // This allows users to press Enter in CSV editors for multi-line descriptions
          const cleanedJSON = row.lineItemsJSON
            .replace(/\r\n/g, '\\n')  // Windows line endings
            .replace(/\n/g, '\\n')     // Unix line endings  
            .replace(/\r/g, '\\n')     // Mac line endings
            .replace(/\t/g, ' ');      // Replace tabs with spaces
          
          const lineItems = JSON.parse(cleanedJSON);
          if (!Array.isArray(lineItems) || lineItems.length === 0) {
            result.errors.push('Line items must be a non-empty JSON array');
            result.valid = false;
          }
        } catch (error) {
          result.errors.push(`Invalid line items JSON format: ${error.message}`);
          result.valid = false;
        }

        // Validate numeric fields
        if (isNaN(parseFloat(row.subtotal))) {
          result.errors.push('Subtotal must be a valid number');
          result.valid = false;
        }
        if (isNaN(parseFloat(row.grandTotal))) {
          result.errors.push('Grand total must be a valid number');
          result.valid = false;
        }

        // Validate optional date fields if provided
        if (row.rentalStartDate && !dateRegex.test(row.rentalStartDate)) {
          result.errors.push('Rental start date must be in YYYY-MM-DD format');
          result.valid = false;
        }
        if (row.rentalEndDate && !dateRegex.test(row.rentalEndDate)) {
          result.errors.push('Rental end date must be in YYYY-MM-DD format');
          result.valid = false;
        }

        validationResults.push(result);
      }

      setValidationResults(validationResults);
    } catch (error) {
      alert(`Error validating data: ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  // Import validated data
  const handleImport = async () => {
    const validRecords = validationResults.filter(r => r.valid);
    if (validRecords.length === 0) {
      alert('No valid records to import');
      return;
    }

    setImporting(true);
    setStep('import');
    setProgress(0);

    try {
      // Prepare data for bulk import
      const quotationsToImport = validRecords.map(record => ({
        quotationNumber: record.data.quotationNumber,
        lineNumber: record.lineNumber,
        data: {
          companyName: record.data.companyName,
          attn: record.data.attn,
          tel: record.data.tel,
          email: record.data.email || '',
          siteAddress: record.data.siteAddress || '',
          billingAddress: record.data.billingAddress || '',
          quotationType: record.data.quotationType,
          quotationDate: record.data.quotationDate,
          rentalStartDate: record.data.rentalStartDate || null,
          rentalEndDate: record.data.rentalEndDate || null,
          deliveryDate: record.data.deliveryDate || null,
          duration: record.data.duration ? parseInt(record.data.duration) : null,
          durationUnit: record.data.durationUnit || null,
          paymentTerms: record.data.paymentTerms,
          customPaymentTerms: record.data.customPaymentTerms || '',
          lineItems: JSON.parse(
            record.data.lineItemsJSON
              .replace(/\r\n/g, '\\n')
              .replace(/\n/g, '\\n')
              .replace(/\r/g, '\\n')
              .replace(/\t/g, ' ')
          ),
          subtotal: parseFloat(record.data.subtotal),
          discountType: record.data.discountType || 'none',
          discountValue: record.data.discountValue ? parseFloat(record.data.discountValue) : 0,
          gstPercentage: record.data.gstPercentage ? parseFloat(record.data.gstPercentage) : 9,
          grandTotal: parseFloat(record.data.grandTotal),
          notes: record.data.notes || '',
          internalRemarks: record.data.internalRemarks || '',
          importedBy: auth.currentUser?.email || 'system'
        }
      }));

      // Call bulk import function
      const importResults = await bulkCreateQuotations(quotationsToImport);
      
      setResults(importResults);
      setStep('complete');

      if (onSuccess) {
        onSuccess(importResults);
      }

    } catch (error) {
      alert(`Import failed: ${error.message}`);
      setStep('validate');
    } finally {
      setImporting(false);
      setProgress(100);
    }
  };

  // Download sample CSV
  const downloadSample = () => {
    const sampleData = [
      'quotationNumber,quotationDate,companyName,attn,tel,email,siteAddress,billingAddress,quotationType,rentalStartDate,rentalEndDate,deliveryDate,duration,durationUnit,paymentTerms,customPaymentTerms,lineItemsJSON,subtotal,discountType,discountValue,gstPercentage,grandTotal,notes,internalRemarks',
      'BS-012024-001,2024-01-15,ABC Construction Pte Ltd,John Tan,91234567,john@abc.com,123 Construction Site,123 Billing Address,rental,2024-01-20,2024-02-20,2024-01-18,1,months,50% deposit, rest upon installation of scaffold,,"[{""description"":""Single width scaffold 4m working height"",""quantity"":5,""unitPrice"":100.00,""total"":500.00}]",500.00,none,0,9,545.00,Special handling required,Customer is VIP',
      'BS-012024-002,2024-01-16,XYZ Builders Ltd,Mary Lim,98765432,mary@xyz.com,456 Building Site,456 Billing Address,purchase,,,2024-01-20,,,100% payment,,"[{""description"":""Double width scaffold 6m working height"",""quantity"":3,""unitPrice"":150.00,""total"":450.00}]",450.00,percentage,10,9,441.45,Rush order,Urgent delivery needed'
    ].join('\n');

    const blob = new Blob([sampleData], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'quotations-import-sample.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const validCount = validationResults.filter(r => r.valid).length;
  const invalidCount = validationResults.filter(r => !r.valid).length;

  return (
    <div className="csv-import-modal">
      <div className="csv-import-container">
        <div className="import-header">
          <h3>📊 CSV Quotation Import</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {/* Step 1: Upload */}
        {step === 'upload' && (
          <div className="import-step">
            <div className="step-content">
              <h4>Step 1: Upload CSV File</h4>
              <p>Upload a CSV file with historical quotation data. All quotation numbers must be unique.</p>
              
              <div className="file-upload-area">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                />
                <button 
                  className="upload-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={processing}
                >
                  {processing ? '⏳ Processing...' : '📁 Select CSV File'}
                </button>
                {file && <p className="file-name">Selected: {file.name}</p>}
              </div>

              <div className="sample-download">
                <button className="sample-btn" onClick={downloadSample}>
                  📥 Download Sample CSV
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Validate */}
        {step === 'validate' && (
          <div className="import-step">
            <div className="step-content">
              <h4>Step 2: Validation Results</h4>
              <div className="validation-summary">
                <div className="summary-item valid">
                  ✅ Valid Records: {validCount}
                </div>
                <div className="summary-item invalid">
                  ❌ Invalid Records: {invalidCount}
                </div>
              </div>

              <div className="validation-details">
                {validationResults.map((result, index) => (
                  <div key={index} className={`validation-row ${result.valid ? 'valid' : 'invalid'}`}>
                    <div className="row-header">
                      <span className="line-number">Line {result.lineNumber}</span>
                      <span className="quotation-number">{result.data.quotationNumber}</span>
                      <span className="company-name">{result.data.companyName}</span>
                      <span className="date">{result.data.quotationDate}</span>
                    </div>
                    {result.errors.length > 0 && (
                      <div className="errors">
                        {result.errors.map((error, i) => (
                          <div key={i} className="error">❌ {error}</div>
                        ))}
                      </div>
                    )}
                    {result.warnings.length > 0 && (
                      <div className="warnings">
                        {result.warnings.map((warning, i) => (
                          <div key={i} className="warning">⚠️ {warning}</div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="step-actions">
                <button className="btn-secondary" onClick={() => setStep('upload')}>
                  ← Back
                </button>
                <button 
                  className="btn-primary"
                  onClick={handleImport}
                  disabled={validCount === 0}
                >
                  Import {validCount} Records →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Import Progress */}
        {step === 'import' && (
          <div className="import-step">
            <div className="step-content">
              <h4>Step 3: Importing Records</h4>
              <div className="import-progress">
                <div className="progress-spinner"></div>
                <p>Importing quotations to Firebase...</p>
                <p>Please wait, this may take a few moments.</p>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Complete */}
        {step === 'complete' && results && (
          <div className="import-step">
            <div className="step-content">
              <h4>Step 4: Import Complete</h4>
              <div className="import-results">
                <div className="result-summary">
                  <div className="result-item success">
                    ✅ Successfully imported: {results.successful} quotations
                  </div>
                  {results.failed > 0 && (
                    <div className="result-item error">
                      ❌ Failed to import: {results.failed} quotations
                    </div>
                  )}
                </div>

                {results.errors.length > 0 && (
                  <div className="error-details">
                    <h5>Import Errors:</h5>
                    {results.errors.map((error, index) => (
                      <div key={index} className="error-item">
                        Line {error.line} ({error.quotationNumber}): {error.error}
                      </div>
                    ))}
                  </div>
                )}

                <div className="next-steps">
                  <h5>Next Steps:</h5>
                  <ul>
                    <li>Check your Quotations list to see imported records</li>
                    <li>All imported quotations are marked as historical imports</li>
                    <li>Original quotation numbers have been preserved</li>
                    <li>You can now edit or update these quotations as needed</li>
                  </ul>
                </div>
              </div>

              <div className="step-actions">
                <button className="btn-primary" onClick={onClose}>
                  Done
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Help Section */}
        <div className="import-help">
          <h5>💡 CSV Format Requirements:</h5>
          <ul>
            <li><strong>quotationNumber:</strong> Format BS-MMYYYY-XXX (e.g., BS-012024-001)</li>
            <li><strong>quotationDate:</strong> Format YYYY-MM-DD (e.g., 2024-01-15)</li>
            <li><strong>quotationType:</strong> purchase, rental, rental_extension, or scaffolding_service</li>
            <li><strong>lineItemsJSON:</strong> JSON array of line items with description, quantity, unitPrice, total</li>
            <li><strong>paymentTerms:</strong> Payment terms description</li>
            <li><strong>subtotal, grandTotal:</strong> Numeric values</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CSVQuotationImport;
